import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { companyLogoSrc, type CompanyLogoValue } from "./company-logo";

// A company's logo, with the monogram fallback ADR company-profiles/0006-company-page-mvp §1
// specifies for a company that has not uploaded one.
//
// ONE FALLBACK, NOT ONE PER SURFACE. This started as a block inside CompanyPage; when /c began
// answering the logo too (genesis delivers it on `myCompanies` as well as on the single-company
// query) the choice was to copy those fifteen lines or to lift them. Copied, the two would drift:
// the "half a logo is no logo" guard, the decorative empty alt and the initials themselves are all
// decisions, and a second copy is a second place to get them wrong.

/**
 * The two rungs this app draws a company at, named for where they are used rather than for their
 * pixels. Closed on purpose: a third size is a design decision, not a call-site detail.
 */
const SIZES = {
  /** The identity block of /c/:id. */
  page: "h-20 w-20 text-2xl",
  /** One row of a list, matching every other avatar rung of that shape. */
  row: "h-10 w-10 text-sm",
} as const;

interface CompanyLogoProps {
  /** The logo as the query returned it. Null or absent renders the monogram. */
  logo?: CompanyLogoValue | null;
  /** The company name, for the initials. Never rendered when a logo exists. */
  name: string;
  size: keyof typeof SIZES;
}

export function CompanyLogo({ logo, name, size }: CompanyLogoProps) {
  const src = companyLogoSrc(logo);
  // A span rather than a div: this sits inside a link on a list row, where only phrasing content
  // belongs, and the page's identity block does not care either way.
  const box = `${SIZES[size]} flex-shrink-0 rounded-full`;

  // Decorative in both branches. The company name is already beside it as a heading or as the row's
  // own text, so an alt or a readable monogram would only make a screen reader say it twice.
  return src ? (
    <img src={src} alt="" className={`${box} object-cover`} />
  ) : (
    <span
      className={`${box} flex items-center justify-center font-serif`}
      style={AVATAR_PLACEHOLDER_STYLE}
      aria-hidden="true"
    >
      {avatarInitials(name)}
    </span>
  );
}
